import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { previewAutomation } from "../api/services.ts"
import { type AutomationFormValues, formValuesToPayload } from "../helpers/form-values"
import { missingChoices } from "../helpers/section-completion"
import type { AutomationOptions, AutomationPreview } from "../types/automation.types"

export type PreviewStatus = "idle" | "loading" | "ready" | "error"

const PREVIEW_DEBOUNCE_MS = 300

// The server only renders email, and only once there is something valid to
// render: values it would reject (a 400) must not flip the preview to an
// error. The same rule as the "Still to choose" list, bar the name, which the
// preview does not use.
const canPreview = (values: AutomationFormValues, options: AutomationOptions) =>
  values.channel === "EMAIL" &&
  missingChoices(values, options).every((choice) => choice.field === "name")

// The server-rendered email for the values the form would submit, debounced,
// with only the newest request allowed to land. `retry` re-sends the same one.
export function useAutomationPreview(
  values: AutomationFormValues,
  options: AutomationOptions,
  leadId: string | null
) {
  const [preview, setPreview] = useState<AutomationPreview | null>(null)
  const [status, setStatus] = useState<PreviewStatus>("idle")
  const [attempt, setAttempt] = useState(0)
  const latestRequest = useRef(0)
  // The attempt the last request went out for: a new one is a Retry click,
  // which should not wait out the typing debounce.
  const sentAttempt = useRef(0)

  // A string key so a parent handing over an equal-but-new object each render
  // does not restart the debounce.
  const requestKey = useMemo(() => {
    if (!canPreview(values, options)) return null
    const payload = formValuesToPayload(values)
    return JSON.stringify({ ...payload, name: payload.name || "preview", lead_id: leadId })
  }, [values, options, leadId])

  useEffect(() => {
    const requestId = ++latestRequest.current
    if (!requestKey) {
      setPreview(null)
      setStatus("idle")
      return
    }
    const retrying = attempt !== sentAttempt.current
    sentAttempt.current = attempt
    const timer = window.setTimeout(async () => {
      setStatus("loading")
      try {
        const response = await previewAutomation(JSON.parse(requestKey))
        // A slower, older response must not overwrite a newer one.
        if (requestId !== latestRequest.current) return
        if (response.ok) {
          setPreview(unwrapEnvelope<AutomationPreview>(response.data))
          setStatus("ready")
        } else {
          setPreview(null)
          setStatus("error")
        }
      } catch {
        if (requestId === latestRequest.current) {
          setPreview(null)
          setStatus("error")
        }
      }
    }, retrying ? 0 : PREVIEW_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      // Invalidates the in-flight request, including on unmount.
      latestRequest.current += 1
    }
  }, [requestKey, attempt])

  const retry = useCallback(() => setAttempt((count) => count + 1), [])

  return { preview, status, retry }
}
